import React, { useRef, useState } from "react";
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaListUl,
  FaLink,
} from "react-icons/fa";
import "../styles/CreateNote.css";

const FOLLOW_UPS = [
  { value: "", label: "No follow-up" },
  { value: "Open", label: "Open follow-up" },
  { value: "In Progress", label: "Follow-up in progress" },
  { value: "Completed", label: "Completed follow-up" },
];

export default function CreateNote({ onClose, onSave }) {
  const editorRef = useRef(null);
  const [recordCorrespond, setRecordCorrespond] = useState("");
  const [toDoTask, setToDoTask] = useState("");
  const [reminderLabel, setReminderLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [activeFormats, setActiveFormats] = useState({});

  const toggleFormat = (command, formatKey) => {
    document.execCommand(command, false, null);
    editorRef.current?.focus();
    setActiveFormats((prev) => ({ ...prev, [formatKey]: !prev[formatKey] }));
  };

  const handleCreate = async () => {
    const value = editorRef.current?.innerHTML.trim() || "";
    const plainText = editorRef.current?.innerText.trim() || "";
    if (!plainText) {
      setError("Add a note before saving.");
      editorRef.current?.focus();
      return;
    }

    setSaving(true);
    setError("");
    try {
      await onSave({
        note: value,
        recordCorrespond: recordCorrespond || undefined,
        toDoTask: toDoTask || undefined,
        reminderLabel: toDoTask && reminderLabel ? reminderLabel : undefined,
      });
    } catch (saveError) {
      setError(saveError?.message || "Unable to save this note.");
      setSaving(false);
    }
  };

  return (
    <div className="overlay" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div className="note-container" role="dialog" aria-modal="true" aria-labelledby="new-note-title">
        <div className="note-header-create">
          <div>
            <h3 id="new-note-title" className="note-title">Create note</h3>
            <p className="note-subtitle">Record useful context for this client and opportunity.</p>
          </div>
          <button type="button" className="note-cross" onClick={onClose} aria-label="Close">×</button>
        </div>

        <label className="note-field-label" htmlFor="sales-note-editor">Note <span>*</span></label>
        <div
          id="sales-note-editor"
          className="note-textarea"
          contentEditable
          ref={editorRef}
          suppressContentEditableWarning
          data-placeholder="Add decisions, client requirements, access details or next steps…"
          onInput={() => error && setError("")}
        />

        <div className="note-toolbar" aria-label="Note formatting">
          <button type="button" title="Bold" className={activeFormats.bold ? "active" : ""} onClick={() => toggleFormat("bold", "bold")}><FaBold /></button>
          <button type="button" title="Italic" className={activeFormats.italic ? "active" : ""} onClick={() => toggleFormat("italic", "italic")}><FaItalic /></button>
          <button type="button" title="Underline" className={activeFormats.underline ? "active" : ""} onClick={() => toggleFormat("underline", "underline")}><FaUnderline /></button>
          <button type="button" title="Bulleted list" className={activeFormats.list ? "active" : ""} onClick={() => toggleFormat("insertUnorderedList", "list")}><FaListUl /></button>
          <button type="button" title="Add link" onClick={() => {
            const url = window.prompt("Enter the link URL");
            if (url) document.execCommand("createLink", false, url);
            editorRef.current?.focus();
          }}><FaLink /></button>
        </div>

        <div className="note-options">
          <div className="note-left">
            <label>Interaction type</label>
            <select className="select" value={recordCorrespond} onChange={(e) => setRecordCorrespond(e.target.value)}>
              <option value="">General note</option>
              <option value="Email">Email</option>
              <option value="Call">Call</option>
              <option value="Meeting">Meeting</option>
              <option value="Site Visit">Site visit</option>
              <option value="Client Request">Client request</option>
            </select>
          </div>
          <div className="note-left">
            <label>Follow-up</label>
            <select className="select" value={toDoTask} onChange={(e) => {
              setToDoTask(e.target.value);
              if (!e.target.value) setReminderLabel("");
            }}>
              {FOLLOW_UPS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </div>
          <div className="note-right">
            <label>Reminder</label>
            <select className="select" value={reminderLabel} disabled={!toDoTask} onChange={(e) => setReminderLabel(e.target.value)}>
              <option value="">No reminder</option>
              <option value="Tomorrow">Tomorrow</option>
              <option value="In 3 business days">In 3 business days</option>
              <option value="Next Week">Next week</option>
            </select>
          </div>
        </div>

        {error ? <p className="note-form-error" role="alert">{error}</p> : null}
        <div className="note-footer">
          <button type="button" className="note-cancel-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="create-btn" onClick={handleCreate} disabled={saving}>{saving ? "Saving…" : "Save note"}</button>
        </div>
      </div>
    </div>
  );
}
